import { useState, useEffect } from 'react';
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Plus,
  Clock,
  Users,
  MapPin,
  AlertCircle,
  CheckCircle,
  Building,
  Truck
} from 'lucide-react';
import '../styles/Calendario.css';

const Calendario = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month'); // month, week, day
  const [themeColor, setThemeColor] = useState('#c4fffc');

  // Actualizar color del tema en tiempo real
  useEffect(() => {
    const updateThemeColor = () => {
      const color = localStorage.getItem('theme-primary-color') || '#c4fffc';
      setThemeColor(color);
    };

    updateThemeColor();
    const interval = setInterval(updateThemeColor, 200);
    window.addEventListener('theme-color-change', updateThemeColor);

    return () => {
      clearInterval(interval);
      window.removeEventListener('theme-color-change', updateThemeColor);
    };
  }, []);

  const events = [
    {
      id: 1,
      titulo: 'Reunión con Minera Escondida',
      fecha: '2025-01-15',
      hora: '09:00',
      duracion: 120,
      tipo: 'reunion',
      cliente: 'Minera Escondida',
      participantes: ['Carlos Mendoza', 'Ana Rodríguez'],
      ubicacion: 'Oficina Santiago',
      estado: 'confirmado',
      descripcion: 'Presentación de propuesta técnica para sistema de bombeo'
    },
    {
      id: 2,
      titulo: 'Entrega EPP - Anglo American',
      fecha: '2025-01-16',
      hora: '14:30',
      duracion: 60,
      tipo: 'entrega',
      cliente: 'Anglo American',
      participantes: ['Miguel Silva'],
      ubicacion: 'Faena Collahuasi',
      estado: 'pendiente',
      descripcion: 'Entrega programada de equipos de protección personal'
    },
    {
      id: 3,
      titulo: 'Inspección de Calidad - BHP',
      fecha: '2025-01-17',
      hora: '10:00',
      duracion: 180,
      tipo: 'inspeccion',
      cliente: 'BHP Billiton',
      participantes: ['Roberto Torres', 'Carlos Mendoza'],
      ubicacion: 'Mina Spence',
      estado: 'confirmado',
      descripcion: 'Inspección de válvulas instaladas en planta'
    },
    {
      id: 4,
      titulo: 'Revisión Financiera Mensual',
      fecha: '2025-01-18',
      hora: '15:00',
      duracion: 90,
      tipo: 'interno',
      cliente: 'Interno',
      participantes: ['Laura González', 'Carlos Mendoza'],
      ubicacion: 'Sala de Reuniones',
      estado: 'confirmado',
      descripcion: 'Revisión de estados financieros y proyecciones'
    },
    {
      id: 5,
      titulo: 'Urgencia: Falla Sistema Bombeo',
      fecha: '2025-01-14',
      hora: '16:00',
      duracion: 240,
      tipo: 'urgencia',
      cliente: 'Minera Escondida',
      participantes: ['Carlos Mendoza', 'Miguel Silva', 'Ana Rodríguez'],
      ubicacion: 'Planta Minera',
      estado: 'en-proceso',
      descripcion: 'Atención inmediata a falla crítica en sistema principal'
    }
  ];

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    return firstDay === 0 ? 6 : firstDay - 1; // Adjust for Monday start
  };

  const getEventsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return events.filter(event => event.fecha === dateStr);
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const getEventTypeIcon = (tipo) => {
    switch(tipo) {
      case 'reunion': return <Users size={14} />;
      case 'entrega': return <Truck size={14} />;
      case 'inspeccion': return <CheckCircle size={14} />;
      case 'urgencia': return <AlertCircle size={14} />;
      case 'interno': return <Building size={14} />;
      default: return <Calendar size={14} />;
    }
  };

  const getEventTypeColor = (tipo) => {
    switch(tipo) {
      case 'reunion': return 'event-meeting';
      case 'entrega': return 'event-delivery';
      case 'inspeccion': return 'event-inspection';
      case 'urgencia': return 'event-urgent';
      case 'interno': return 'event-internal';
      default: return 'event-default';
    }
  };

  const getStatusColor = (estado) => {
    switch(estado) {
      case 'confirmado': return 'status-confirmed';
      case 'pendiente': return 'status-pending';
      case 'en-proceso': return 'status-progress';
      case 'completado': return 'status-completed';
      default: return 'status-pending';
    }
  };

  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  const dayNames = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

  const renderCalendarGrid = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];

    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
      const dayEvents = getEventsForDate(date);
      const isToday = date.toDateString() === new Date().toDateString();
      const isSelected = date.toDateString() === selectedDate.toDateString();

      days.push(
        <div
          key={day}
          className={`calendar-day ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''} ${dayEvents.length > 0 ? 'has-events' : ''}`}
          onClick={() => setSelectedDate(date)}
          style={{
            backgroundColor: isToday ? themeColor : undefined,
            border: isToday ? '2px solid #000' : undefined,
            cursor: 'pointer'
          }}
        >
          <div className="day-number" style={{
            color: isToday ? '#000' : undefined,
            fontWeight: isToday ? '700' : undefined
          }}>{day}</div>
          {dayEvents.length > 0 && (
            <div className="day-events">
              {dayEvents.slice(0, 2).map((event, index) => (
                <div
                  key={event.id}
                  className={`event-dot ${getEventTypeColor(event.tipo)}`}
                  title={event.titulo}
                >
                  {getEventTypeIcon(event.tipo)}
                </div>
              ))}
              {dayEvents.length > 2 && (
                <div className="more-events">+{dayEvents.length - 2}</div>
              )}
            </div>
          )}
        </div>
      );
    }

    return days;
  };

  return (
    <div className="calendario-container">
      <div className="calendario-header">
        <div className="header-title">
          <Calendar size={28} />
          <h1>Calendario</h1>
        </div>
        <div className="header-actions">
          <button
            className="btn-nuevo-evento"
            style={{
              backgroundColor: themeColor,
              color: '#000',
              border: '2px solid #000'
            }}
          >
            <Plus size={20} />
            Nuevo Evento
          </button>
        </div>
      </div>

      <div className="calendario-stats">
        <div className="stat-card">
          <div className="stat-icon">
            <Calendar size={20} />
          </div>
          <div className="stat-content">
            <p>Eventos Hoy</p>
            <h3>{getEventsForDate(new Date()).length}</h3>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon warning">
            <Clock size={20} />
          </div>
          <div className="stat-content">
            <p>Pendientes</p>
            <h3>{events.filter(e => e.estado === 'pendiente').length}</h3>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon urgent">
            <AlertCircle size={20} />
          </div>
          <div className="stat-content">
            <p>Urgencias</p>
            <h3>{events.filter(e => e.tipo === 'urgencia').length}</h3>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon success">
            <CheckCircle size={20} />
          </div>
          <div className="stat-content">
            <p>Completados</p>
            <h3>{events.filter(e => e.estado === 'completado').length}</h3>
          </div>
        </div>
      </div>

      <div className="calendario-content">
        <div className="calendar-view">
          <div className="calendar-controls">
            <button className="nav-btn" onClick={() => navigateMonth(-1)}>
              <ChevronLeft size={20} />
            </button>
            <h2 className="month-year">
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </h2>
            <button className="nav-btn" onClick={() => navigateMonth(1)}>
              <ChevronRight size={20} />
            </button>
          </div>

          <div className="calendar-grid">
            <div className="calendar-header-row">
              {dayNames.map(day => (
                <div key={day} className="calendar-header-day" style={{ color: '#000', padding: '10px', fontWeight: '600' }}>
                  {day}
                </div>
              ))}
            </div>
            <div className="calendar-body">
              {renderCalendarGrid()}
            </div>
          </div>
        </div>

        <div className="events-sidebar">
          <div className="sidebar-header">
            <h3>
              {selectedDate.toLocaleDateString('es-CL', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </h3>
          </div>

          <div className="events-list">
            {getEventsForDate(selectedDate).length === 0 ? (
              <div className="no-events">
                <Calendar size={32} />
                <p>No hay eventos programados</p>
              </div>
            ) : (
              getEventsForDate(selectedDate).map((event) => (
                <div key={event.id} className={`event-card ${getEventTypeColor(event.tipo)}`}>
                  <div className="event-header">
                    <div className="event-time">
                      <Clock size={14} />
                      <span>{event.hora}</span>
                    </div>
                    <span className={`event-status ${getStatusColor(event.estado)}`}>
                      {event.estado}
                    </span>
                  </div>

                  <div className="event-content">
                    <div className="event-title-row">
                      {getEventTypeIcon(event.tipo)}
                      <h4>{event.titulo}</h4>
                    </div>

                    <div className="event-details">
                      <div className="detail-row">
                        <Building size={12} />
                        <span>{event.cliente}</span>
                      </div>
                      <div className="detail-row">
                        <MapPin size={12} />
                        <span>{event.ubicacion}</span>
                      </div>
                      <div className="detail-row">
                        <Users size={12} />
                        <span>{event.participantes.join(', ')}</span>
                      </div>
                    </div>

                    <p className="event-description">{event.descripcion}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Calendario;